import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/joy';
import { motion } from 'framer-motion';
import WebhookIcon from '@mui/icons-material/Webhook';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

const WebhooksEmptyState = ({ onAddNew }) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 4,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Stack alignItems="center" spacing={3}>
          {/* Icon */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.vars.palette.primary[100]} 0%, ${theme.vars.palette.primary[200]} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'sm',
            }}
          >
            <WebhookIcon
              sx={{
                fontSize: 48,
                color: 'primary.500',
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            level="h3"
            sx={{
              textAlign: 'center',
              color: 'text.primary',
            }}
          >
            No webhooks configured
          </Typography>

          {/* Description */}
          <Typography
            level="body-md"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              maxWidth: 400,
            }}
          >
            Set up webhooks to automatically receive extraction results via HTTP POST requests to your endpoints.
          </Typography>

          {/* Features list */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              mt: 1,
              p: 2,
              bgcolor: 'background.level1',
              borderRadius: 'lg',
            }}
          >
            <FeatureItem
              title="Real-time delivery"
              description="Get results instantly"
            />
            <FeatureItem
              title="Field mapping"
              description="Customize output format"
            />
            <FeatureItem
              title="Secure auth"
              description="Basic auth support"
            />
          </Stack>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <Button
              size="lg"
              startDecorator={<AddRoundedIcon />}
              onClick={onAddNew}
              sx={{ mt: 2 }}
            >
              Create your first webhook
            </Button>
          </motion.div>
        </Stack>
      </motion.div>
    </Box>
  );
};

const FeatureItem = ({ title, description }) => (
  <Stack
    alignItems="center"
    spacing={0.5}
    sx={{
      px: 2,
      py: 1,
      minWidth: 120,
    }}
  >
    <Typography
      level="title-sm"
      sx={{
        fontWeight: 600,
        color: 'text.primary',
        textAlign: 'center',
      }}
    >
      {title}
    </Typography>
    <Typography
      level="body-xs"
      sx={{
        color: 'text.tertiary',
        textAlign: 'center',
      }}
    >
      {description}
    </Typography>
  </Stack>
);

export default WebhooksEmptyState;
